import { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import type {
  EnvironmentId,
  ProjectId,
  ProjectDocumentFile,
  ProjectDocumentsStatusResult,
  ProjectOutputFile,
  ProjectOutputStatusResult,
} from "@t3tools/contracts";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
  type RowSelectionState,
  type SortingState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import * as Schema from "effect/Schema";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  EllipsisIcon,
  LoaderIcon,
  RefreshCwIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import {
  buildArtifactRoutePath,
  countDocumentPages,
  filterDocumentFiles,
} from "./DocumentsSidebar.logic";
import { ScrollArea } from "./ui/scroll-area";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "./ui/menu";
import { Sheet, SheetPopup } from "./ui/sheet";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { cn } from "~/lib/utils";
import { readEnvironmentApi } from "~/environmentApi";
import { DEVICE_STORAGE_KEYS } from "~/accountStorage";
import { useLocalStorage } from "~/hooks/useLocalStorage";
import { i18n } from "~/i18n";
import { stackedThreadToast, toastManager } from "./ui/toast";
import { useTranslation } from "react-i18next";

const SIDEBAR_WIDTH_STORAGE_KEY = DEVICE_STORAGE_KEYS.documentsPanelWidth;
const SORTING_STORAGE_KEY = "t3code:documents-panel-sorting:v1";

const DEFAULT_SORTING: SortingState = [{ id: "status", desc: false }];

const SortingStateSchema = Schema.mutable(
  Schema.Array(Schema.Struct({ id: Schema.String, desc: Schema.Boolean })),
);
const SIDEBAR_MIN_WIDTH = 320;
const SIDEBAR_DEFAULT_WIDTH = 600;

function clampSidebarWidth(width: number): number {
  const safeWidth = Number.isFinite(width) ? width : SIDEBAR_DEFAULT_WIDTH;
  const maxWidth =
    typeof window === "undefined"
      ? SIDEBAR_DEFAULT_WIDTH
      : Math.max(SIDEBAR_MIN_WIDTH, Math.round(window.innerWidth * 0.6));
  return Math.min(Math.max(safeWidth, SIDEBAR_MIN_WIDTH), maxWidth);
}

function readStoredSidebarWidth(): number {
  if (typeof window === "undefined") return SIDEBAR_DEFAULT_WIDTH;
  const raw = window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
  const parsed = raw === null ? Number.NaN : Number.parseInt(raw, 10);
  return clampSidebarWidth(Number.isNaN(parsed) ? SIDEBAR_DEFAULT_WIDTH : parsed);
}

function formatDocumentSize(size: number | null): string | null {
  if (size === null) return null;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function formatModifiedAt(mtimeMs: number | null): string | null {
  if (mtimeMs === null) return null;
  return new Date(mtimeMs)
    .toLocaleString(i18n.language, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(/\s*г\./, "");
}

function fileBasename(path: string): string {
  const separatorIndex = path.lastIndexOf("/");
  return separatorIndex >= 0 ? path.slice(separatorIndex + 1) : path;
}

type DocumentRow = ProjectDocumentFile & { copies?: DocumentRow[] };

function buildDocumentRows(files: ProjectDocumentFile[]): DocumentRow[] {
  const byHash = new Map<string, ProjectDocumentFile[]>();
  const rows: DocumentRow[] = [];
  for (const file of files) {
    if (!file.sha256) {
      rows.push(file);
      continue;
    }
    const bucket = byHash.get(file.sha256);
    if (bucket) {
      bucket.push(file);
    } else {
      byHash.set(file.sha256, [file]);
    }
  }
  for (const bucket of byHash.values()) {
    if (bucket.length === 1) {
      rows.push(bucket[0]!);
      continue;
    }
    const ordered = bucket
      .slice()
      .sort((a, b) => a.path.length - b.path.length || a.path.localeCompare(b.path));
    const [original, ...copies] = ordered;
    rows.push({ ...original!, copies });
  }
  return rows;
}

const COLUMN_WIDTH: Record<string, string> = {
  select: "2rem",
  status: "6rem",
  pages: "4rem",
  size: "5rem",
  modified: "9.5rem",
};

type DisplayStatus = "ready" | "pending" | "error" | "unsupported";

const DISPLAY_STATUS_RANK: Record<DisplayStatus, number> = {
  ready: 3,
  pending: 2,
  error: 1,
  unsupported: 0,
};

function displayStatus(file: ProjectDocumentFile): DisplayStatus {
  if (file.state === "ok") return "ready";
  if (file.state === "failed") return "error";
  if (file.state === "unsupported") return "unsupported";
  return "pending";
}

const STATUS_BADGE_CLASS: Record<DisplayStatus, string> = {
  ready: "bg-emerald-500/10 text-emerald-500",
  pending: "bg-muted/60 text-muted-foreground",
  error: "bg-red-500/10 text-red-500",
  unsupported: "bg-muted/40 text-muted-foreground/60",
};

interface DocumentsPanelProps {
  open: boolean;
  environmentId: EnvironmentId;
  projectId: ProjectId;
  projectCwd: string | undefined;
  onClose: () => void;
}

const columnHelper = createColumnHelper<DocumentRow>();

const DocumentsPanel = memo(function DocumentsPanel({
  open,
  environmentId,
  projectId,
  projectCwd,
  onClose,
}: DocumentsPanelProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<"materials" | "created">("materials");
  const [status, setStatus] = useState<ProjectDocumentsStatusResult | null>(null);
  const [outputStatus, setOutputStatus] = useState<ProjectOutputStatusResult | null>(null);
  const [sidebarWidth, setSidebarWidth] = useState(readStoredSidebarWidth);
  const sidebarWidthRef = useRef(sidebarWidth);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const resizeStateRef = useRef<{
    pointerId: number;
    startX: number;
    startWidth: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [sorting, setSorting] = useLocalStorage(
    SORTING_STORAGE_KEY,
    DEFAULT_SORTING,
    SortingStateSchema,
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [searchInput, setSearchInput] = useState("");
  const searchQuery = useDeferredValue(searchInput);

  const exitSelectionMode = useCallback(() => {
    setIsSelectionMode(false);
    setRowSelection({});
  }, []);

  const handleResizePointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    resizeStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidth: sidebarWidthRef.current,
    };
  }, []);

  const handleResizePointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const resizeState = resizeStateRef.current;
    if (!resizeState || resizeState.pointerId !== event.pointerId) return;
    event.preventDefault();
    const nextWidth = clampSidebarWidth(
      resizeState.startWidth + (resizeState.startX - event.clientX),
    );
    if (nextWidth === sidebarWidthRef.current) return;
    sidebarWidthRef.current = nextWidth;
    if (popupRef.current !== null) popupRef.current.style.width = `${nextWidth}px`;
  }, []);

  const handleResizePointerEnd = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const resizeState = resizeStateRef.current;
    if (!resizeState || resizeState.pointerId !== event.pointerId) return;
    resizeStateRef.current = null;
    setSidebarWidth(sidebarWidthRef.current);
    window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(sidebarWidthRef.current));
  }, []);

  const refresh = useCallback(() => {
    const api = readEnvironmentApi(environmentId);
    if (!api || !projectCwd) return;
    setIsLoading(true);
    setLoadError(null);
    void Promise.allSettled([
      api.projects.documentsStatus({ projectId }),
      api.projects.outputStatus({ projectId }),
    ]).then(([documentsResult, outputResult]) => {
      if (documentsResult.status === "fulfilled") setStatus(documentsResult.value);
      if (outputResult.status === "fulfilled") setOutputStatus(outputResult.value);
      const failure =
        documentsResult.status === "rejected"
          ? documentsResult.reason
          : outputResult.status === "rejected"
            ? outputResult.reason
            : null;
      if (failure !== null) {
        setLoadError(
          failure instanceof Error ? failure.message : t("common/anErrorOccurred_gd0s0s"),
        );
      }
      setIsLoading(false);
    });
  }, [environmentId, projectCwd, projectId, t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openDocument = useCallback(
    (routePath: string | null) => {
      const api = readEnvironmentApi(environmentId);
      if (!routePath || !api) {
        toastManager.add({
          type: "error",
          title: t("common/unableToOpenFile_zu4ang"),
        });
        return;
      }
      void api.shell
        .openArtifact({ projectId, path: buildArtifactRoutePath(routePath) })
        .catch((error) => {
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: t("common/unableToOpenFile_zu4ang"),
              description:
                error instanceof Error ? error.message : t("common/anErrorOccurred_gd0s0s"),
            }),
          );
        });
    },
    [environmentId, projectId, t],
  );

  const deleteFiles = useCallback(
    (files: ProjectDocumentFile[]) => {
      const api = readEnvironmentApi(environmentId);
      if (!api || !projectCwd || files.length === 0) return;
      const confirmation =
        files.length === 1
          ? t("common/deleteFromTheCase_gd7487", { "0": files[0]?.path })
          : t("common/deleteFilesFromThe_efu8on", { "0": files.length });
      if (!window.confirm(confirmation)) return;
      setIsDeleting(true);
      void (async () => {
        const failures: string[] = [];
        for (const [index, file] of files.entries()) {
          try {
            await api.projects.deleteDocument({
              projectId,
              relativePath: file.path,
              reconcile: index === files.length - 1,
            });
          } catch {
            failures.push(file.path);
          }
        }
        if (failures.length === 0) {
          toastManager.add({
            type: "success",
            title:
              files.length === 1
                ? t("common/documentDeleted_9bosql")
                : t("common/documentsDeleted_zznqy9", { "0": files.length }),
          });
        } else {
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: t("common/couldNotDeleteOf_diel5o", {
                "0": failures.length,
                "1": files.length,
              }),
              description: failures.slice(0, 3).join(", "),
            }),
          );
        }
        setRowSelection({});
        setIsDeleting(false);
        refresh();
      })();
    },
    [environmentId, projectCwd, projectId, refresh, t],
  );

  const copyOutputToMaterials = useCallback(
    (file: ProjectOutputFile) => {
      const api = readEnvironmentApi(environmentId);
      if (!api || !projectCwd) return;
      setIsDeleting(true);
      void api.projects
        .copyOutputToMaterials({ projectId, relativePath: file.path })
        .then(() => {
          toastManager.add({ type: "success", title: t("common/fileAddedToMaterials_hvtevw") });
          refresh();
        })
        .catch((error) => {
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: t("common/couldNotAddThe_ycvu9r"),
              description:
                error instanceof Error ? error.message : t("common/anErrorOccurred_gd0s0s"),
            }),
          );
        })
        .then(
          () => setIsDeleting(false),
          () => setIsDeleting(false),
        );
    },
    [environmentId, projectCwd, projectId, refresh, t],
  );

  const deleteOutputFile = useCallback(
    (file: ProjectOutputFile) => {
      const api = readEnvironmentApi(environmentId);
      if (!api || !projectCwd) return;
      if (!window.confirm(t("common/deleteCreatedFile_lcn961", { "0": file.path }))) return;
      setIsDeleting(true);
      void api.projects
        .deleteOutputFile({ projectId, relativePath: file.path })
        .then(() => {
          toastManager.add({ type: "success", title: t("common/fileDeleted_smelbz") });
          refresh();
        })
        .catch((error) => {
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: t("common/anErrorOccurred_gd0s0s"),
              description: error instanceof Error ? error.message : undefined,
            }),
          );
        })
        .then(
          () => setIsDeleting(false),
          () => setIsDeleting(false),
        );
    },
    [environmentId, projectCwd, projectId, refresh, t],
  );

  const files = useMemo(
    () =>
      filterDocumentFiles(
        (status?.files ?? []).filter((file) => file.state !== "orphaned"),
        searchQuery,
      ),
    [status?.files, searchQuery],
  );

  const createdFiles = useMemo(
    () => filterDocumentFiles(outputStatus?.files.slice() ?? [], searchQuery),
    [outputStatus?.files, searchQuery],
  );

  const rows = useMemo(() => buildDocumentRows(files), [files]);

  const statusLabels: Record<DisplayStatus, string> = useMemo(
    () => ({
      ready: t("common/ready_qgha2f"),
      pending: t("common/pending_shp0pq"),
      error: t("common/error_z8ql4u"),
      unsupported: t("common/unsupported_4jn4de"),
    }),
    [t],
  );

  const columns = useMemo(
    () => [
      ...(isSelectionMode
        ? [
            columnHelper.display({
              id: "select",
              header: ({ table }) => (
                <Checkbox
                  checked={table.getIsAllRowsSelected()}
                  indeterminate={table.getIsSomeRowsSelected()}
                  onCheckedChange={(checked) => table.toggleAllRowsSelected(checked === true)}
                  aria-label={t("common/selectAllDocuments_9qjfit")}
                />
              ),
              cell: ({ row }) => (
                <Checkbox
                  checked={row.getIsSelected()}
                  onCheckedChange={(checked) => row.toggleSelected(checked === true)}
                  aria-label={t("common/selectDocument_45ww0b")}
                />
              ),
            }),
          ]
        : []),
      columnHelper.accessor((file) => fileBasename(file.path), {
        id: "name",
        header: () => t("common/name_p0qla7"),
        cell: ({ row, getValue }) => (
          <span
            className={cn("flex min-w-0 items-center gap-1.5", row.depth > 0 && "pl-4")}
            title={row.original.path}
          >
            <span className={cn("truncate text-[12px]", row.depth > 0 && "text-muted-foreground")}>
              {getValue()}
            </span>
            {row.depth > 0 ? (
              <Badge
                variant="secondary"
                className="shrink-0 rounded-md bg-muted/60 px-1.5 py-0 text-[10px] text-muted-foreground/70"
              >
                {t("common/copy_pi8oo7")}
              </Badge>
            ) : null}
          </span>
        ),
      }),
      columnHelper.accessor((file) => DISPLAY_STATUS_RANK[displayStatus(file)], {
        id: "status",
        header: () => t("common/status_6gtym5"),
        sortDescFirst: true,
        cell: ({ row }) => {
          const display = displayStatus(row.original);
          return (
            <Badge
              variant="secondary"
              className={cn(
                "rounded-md px-1.5 py-0 text-[10px] font-medium",
                STATUS_BADGE_CLASS[display],
              )}
              title={display === "error" ? (row.original.error ?? undefined) : undefined}
            >
              {statusLabels[display]}
            </Badge>
          );
        },
      }),
      columnHelper.accessor((file) => file.pageCount ?? -1, {
        id: "pages",
        header: () => t("common/pages_o3xtim"),
        cell: ({ row }) => (
          <span className="block text-right text-[12px] tabular-nums text-muted-foreground">
            {row.original.pageCount ?? "—"}
          </span>
        ),
      }),
      columnHelper.accessor((file) => file.size ?? -1, {
        id: "size",
        header: () => t("common/size_7pbasz"),
        cell: ({ row }) => (
          <span className="block text-right text-[12px] tabular-nums text-muted-foreground">
            {formatDocumentSize(row.original.size) ?? "—"}
          </span>
        ),
      }),
      columnHelper.accessor((file) => file.mtimeMs ?? 0, {
        id: "modified",
        header: () => t("common/modified_cfgu0k"),
        sortDescFirst: true,
        cell: ({ row }) => (
          <span className="flex items-center pr-6 text-[12px] whitespace-nowrap text-muted-foreground">
            {formatModifiedAt(row.original.mtimeMs) ?? "—"}
            <span className="absolute top-1/2 right-1 -translate-y-1/2">
              <Menu>
                <MenuTrigger
                  render={
                    <Button
                      size="icon-xs"
                      variant="ghost"
                      className="text-muted-foreground/40 opacity-0 group-hover/row:opacity-100 aria-expanded:opacity-100 hover:text-foreground/70"
                      aria-label={t("common/documentActions_v1gtdj")}
                    />
                  }
                >
                  <EllipsisIcon className="size-3.5" />
                </MenuTrigger>
                <MenuPopup align="end">
                  <MenuItem onClick={() => openDocument(row.original.path)}>
                    {t("chat/open_oekw3l")} {row.original.path.split("/").at(-1)}
                  </MenuItem>
                  {row.original.resultPath !== null ? (
                    <MenuItem onClick={() => openDocument(row.original.resultPath)}>
                      {t("chat/open_oekw3l")} {row.original.resultPath.split("/").at(-1)}
                    </MenuItem>
                  ) : null}
                  <MenuItem onClick={() => deleteFiles([row.original])} disabled={isDeleting}>
                    {t("common/deleteFromCase_lczelt")}
                  </MenuItem>
                </MenuPopup>
              </Menu>
            </span>
          </span>
        ),
      }),
    ],
    [deleteFiles, isDeleting, isSelectionMode, openDocument, statusLabels, t],
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, rowSelection, expanded: true },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => row.path,
    getSubRows: (row) => row.copies,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    enableRowSelection: true,
    enableSubRowSelection: false,
  });

  const selectedFiles = table.getSelectedRowModel().rows.map((row) => row.original);
  const materialsSummary = useMemo(() => {
    if (!status) return null;
    const documents = status.files.filter((file) => file.state !== "orphaned");
    if (documents.length === 0) return null;
    const pending = status.counts.unparsed + status.counts.stale;
    const failed = status.counts.failed;
    const pages = countDocumentPages(documents);
    const left = [t("common/document_dbz3fj", { count: documents.length })];
    if (pages > 0) {
      left.push(t("common/page_7nggn3", { count: pages }));
    }
    const right = [t("common/pending_j39tme", { pending })];
    if (failed > 0) {
      right.push(t("common/error_wqjhft", { count: failed }));
    }
    return { left: left.join(" · "), right: right.join(" · ") };
  }, [status, t]);
  const createdSummary = useMemo(() => {
    if (!outputStatus || outputStatus.files.length === 0) return null;
    return { left: t("common/total_z82mvm", { total: outputStatus.files.length }), right: null };
  }, [outputStatus, t]);
  const activeSummary = activeTab === "materials" ? materialsSummary : createdSummary;

  const tableRows = table.getRowModel().rows;
  const rowVirtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => viewportRef.current,
    estimateSize: () => 33,
    overscan: 12,
    // Disabling while closed drops the virtualizer's cached scroll offset and
    // observers; otherwise a scrollTop reset on the hidden keepMounted popup is
    // missed and the reopened list renders shifted by a stale offset.
    enabled: open,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0]!.start : 0;
  const paddingBottom =
    virtualRows.length > 0 ? rowVirtualizer.getTotalSize() - virtualRows.at(-1)!.end : 0;

  useEffect(() => {
    rowVirtualizer.scrollToOffset(0);
  }, [searchQuery, environmentId, projectCwd, rowVirtualizer]);

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <SheetPopup
        ref={popupRef}
        side="right"
        showCloseButton={false}
        keepMounted
        className="min-w-80 max-w-none p-0 wco:mt-[env(titlebar-area-height)] wco:h-[calc(100%-env(titlebar-area-height))] wco:max-h-[calc(100%-env(titlebar-area-height))]"
        style={{ width: sidebarWidth }}
      >
        <div className="relative flex h-full min-h-0 w-full flex-col">
          <div
            className="absolute inset-y-0 left-0 z-30 w-1 cursor-col-resize touch-none transition-colors hover:bg-primary/20 active:bg-primary/30"
            onPointerDown={handleResizePointerDown}
            onPointerMove={handleResizePointerMove}
            onPointerUp={handleResizePointerEnd}
            onPointerCancel={handleResizePointerEnd}
            aria-hidden="true"
          />
          {/* Header */}
          <div className="flex h-12 shrink-0 items-center justify-between border-b border-border/60 px-3">
            <div className="flex items-center gap-2">
              {(
                [
                  ["materials", t("common/materials_bflxbd")],
                  ["created", t("common/created_av9gvw")],
                ] as const
              ).map(([tab, label]) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    "rounded-md px-1.5 py-0 text-[10px] font-semibold tracking-wide uppercase",
                    activeTab === tab
                      ? "bg-amber-500/10 text-amber-500"
                      : "text-muted-foreground/50 hover:text-foreground/70",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="size-7 shrink-0"
                onClick={refresh}
                disabled={isLoading || !projectCwd}
                aria-label={t("common/refreshDocuments_gat5b0")}
              >
                <RefreshCwIcon className={cn("size-4", isLoading && "animate-spin")} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 shrink-0"
                onClick={onClose}
                aria-label={t("common/closeDocumentsPanel_y94emj")}
              >
                <XIcon className="size-4" />
              </Button>
            </div>
          </div>

          {/* Toolbar */}
          <div className="flex h-9 shrink-0 items-center justify-between gap-2 border-b border-border/60 bg-muted/30 px-3">
            <Input
              size="sm"
              type="search"
              placeholder={t("common/searchDocuments_87x38t")}
              value={searchInput}
              onValueChange={(value) => setSearchInput(value)}
              className="max-w-56 flex-1 text-sm sm:text-xs"
              disabled={!projectCwd}
            />
            <div className="flex shrink-0 items-center gap-2">
              {activeTab !== "materials" ? null : isSelectionMode ? (
                <>
                  {selectedFiles.length > 0 ? (
                    <span className="text-[12px] whitespace-nowrap text-muted-foreground">
                      {t("common/selected_w2emod", { "0": selectedFiles.length })}
                    </span>
                  ) : null}
                  <Button
                    size="xs"
                    variant="destructive"
                    onClick={() => deleteFiles(selectedFiles)}
                    disabled={isDeleting || selectedFiles.length === 0}
                  >
                    {isDeleting ? (
                      <LoaderIcon className="size-3 animate-spin" />
                    ) : (
                      <Trash2Icon className="size-3" />
                    )}
                    {t("common/delete_ivk488")}
                  </Button>
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={exitSelectionMode}
                    disabled={isDeleting}
                  >
                    {t("common/cancel_8mt5fw")}
                  </Button>
                </>
              ) : (
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => setIsSelectionMode(true)}
                  disabled={files.length === 0}
                >
                  {t("common/select_slgwkm")}
                </Button>
              )}
            </div>
          </div>

          {/* Column headers */}
          {activeTab === "materials" &&
          projectCwd &&
          status !== null &&
          files.length > 0 &&
          loadError === null ? (
            <div className="shrink-0">
              <table className="w-full table-fixed text-xs">
                <colgroup>
                  {table.getVisibleLeafColumns().map((column) => (
                    <col key={column.id} style={{ width: COLUMN_WIDTH[column.id] }} />
                  ))}
                </colgroup>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id} className="hover:bg-transparent">
                      {headerGroup.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          className={cn(
                            "h-8 px-2 text-[11px] font-semibold tracking-wide text-muted-foreground/60 uppercase",
                            header.column.id === "select" && "w-8",
                            header.column.id === "status" && "w-24",
                            header.column.id === "pages" && "w-16",
                            header.column.id === "size" && "w-20",
                            header.column.id === "modified" && "w-[9.5rem]",
                          )}
                        >
                          {header.column.getCanSort() ? (
                            <button
                              type="button"
                              className="flex w-full items-center gap-0.5 hover:text-foreground/80"
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {header.column.getIsSorted() === "asc" ? (
                                <ChevronUpIcon className="size-3.5 shrink-0" strokeWidth={2.5} />
                              ) : header.column.getIsSorted() === "desc" ? (
                                <ChevronDownIcon className="size-3.5 shrink-0" strokeWidth={2.5} />
                              ) : null}
                            </button>
                          ) : (
                            flexRender(header.column.columnDef.header, header.getContext())
                          )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
              </table>
            </div>
          ) : null}

          {/* Content */}
          <ScrollArea className="min-h-0 flex-1" viewportRef={viewportRef}>
            {!projectCwd ? (
              <p className="px-4 py-10 text-center text-[12px] text-muted-foreground/40">
                {t("common/documentsAreUnavailableUntil_ham7zm")}
              </p>
            ) : loadError ? (
              <p className="px-4 py-10 text-center text-[12px] text-red-400/80">{loadError}</p>
            ) : activeTab === "created" ? (
              outputStatus === null ? (
                <div className="flex items-center justify-center py-12 text-muted-foreground/40">
                  <LoaderIcon className="size-4 animate-spin" />
                </div>
              ) : createdFiles.length === 0 ? (
                searchQuery.trim().length > 0 ? (
                  <p className="px-4 py-10 text-center text-[12px] text-muted-foreground/40">
                    {t("common/noDocumentsMatch_blk4ad", { "0": searchQuery.trim() })}
                  </p>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <p className="text-[12px] text-muted-foreground/40">
                      {t("common/noCreatedFilesYet_caukni")}
                    </p>
                    <p className="mt-1 text-[11px] text-muted-foreground/30">
                      {t("common/filesTheAssistantCreates_51frn5")}
                    </p>
                  </div>
                )
              ) : (
                <table className="w-full table-fixed caption-bottom text-xs">
                  <colgroup>
                    <col />
                    <col style={{ width: COLUMN_WIDTH["size"] }} />
                    <col style={{ width: COLUMN_WIDTH["modified"] }} />
                  </colgroup>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      {[
                        t("common/name_p0qla7"),
                        t("common/size_7pbasz"),
                        t("common/modified_cfgu0k"),
                      ].map((header) => (
                        <TableHead
                          key={header}
                          className="h-8 px-2 text-[11px] font-semibold tracking-wide text-muted-foreground/60 uppercase"
                        >
                          {header}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {createdFiles.map((file) => (
                      <TableRow key={file.path} className="group/row">
                        <TableCell className="px-2 py-1.5">
                          <span className="flex min-w-0 items-center" title={file.path}>
                            <span className="truncate text-[12px]">{file.path}</span>
                          </span>
                        </TableCell>
                        <TableCell className="px-2 py-1.5">
                          <span className="block text-right text-[12px] tabular-nums text-muted-foreground">
                            {formatDocumentSize(file.size) ?? "—"}
                          </span>
                        </TableCell>
                        <TableCell className="relative px-2 py-1.5">
                          <span className="flex items-center pr-6 text-[12px] whitespace-nowrap text-muted-foreground">
                            {formatModifiedAt(file.mtimeMs) ?? "—"}
                            <span className="absolute top-1/2 right-1 -translate-y-1/2">
                              <Menu>
                                <MenuTrigger
                                  render={
                                    <Button
                                      size="icon-xs"
                                      variant="ghost"
                                      className="text-muted-foreground/40 opacity-0 group-hover/row:opacity-100 aria-expanded:opacity-100 hover:text-foreground/70"
                                      aria-label={t("common/documentActions_v1gtdj")}
                                    />
                                  }
                                >
                                  <EllipsisIcon className="size-3.5" />
                                </MenuTrigger>
                                <MenuPopup align="end">
                                  <MenuItem
                                    onClick={() =>
                                      openDocument(
                                        outputStatus === null
                                          ? null
                                          : `${outputStatus.artifactsPrefix}/${file.path}`,
                                      )
                                    }
                                  >
                                    {t("chat/open_oekw3l")} {fileBasename(file.path)}
                                  </MenuItem>
                                  <MenuItem
                                    onClick={() => copyOutputToMaterials(file)}
                                    disabled={isDeleting}
                                  >
                                    {t("common/addToMaterials_ie96cp")}
                                  </MenuItem>
                                  <MenuItem
                                    onClick={() => deleteOutputFile(file)}
                                    disabled={isDeleting}
                                  >
                                    {t("common/delete_ivk488")}
                                  </MenuItem>
                                </MenuPopup>
                              </Menu>
                            </span>
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </table>
              )
            ) : status === null ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground/40">
                <LoaderIcon className="size-4 animate-spin" />
              </div>
            ) : files.length === 0 ? (
              searchQuery.trim().length > 0 ? (
                <p className="px-4 py-10 text-center text-[12px] text-muted-foreground/40">
                  {t("common/noDocumentsMatch_blk4ad", { "0": searchQuery.trim() })}
                </p>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <p className="text-[12px] text-muted-foreground/40">
                    {t("common/noDocumentsInThis_ljuitb")}
                  </p>
                  <p className="mt-1 text-[11px] text-muted-foreground/30">
                    {t("common/attachFilesInThe_s6mmpk")}
                  </p>
                </div>
              )
            ) : (
              <table className="w-full table-fixed caption-bottom text-xs">
                <colgroup>
                  {table.getVisibleLeafColumns().map((column) => (
                    <col key={column.id} style={{ width: COLUMN_WIDTH[column.id] }} />
                  ))}
                </colgroup>
                <TableBody>
                  {paddingTop > 0 ? <tr aria-hidden style={{ height: paddingTop }} /> : null}
                  {virtualRows.map((virtualRow) => {
                    const row = tableRows[virtualRow.index]!;
                    return (
                      <TableRow
                        key={row.id}
                        ref={rowVirtualizer.measureElement}
                        data-index={virtualRow.index}
                        className="group/row"
                        data-state={row.getIsSelected() ? "selected" : undefined}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            className={cn(
                              "px-2 py-1.5",
                              cell.column.id === "modified" && "relative",
                            )}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                  {paddingBottom > 0 ? <tr aria-hidden style={{ height: paddingBottom }} /> : null}
                </TableBody>
              </table>
            )}
          </ScrollArea>

          {/* Status bar */}
          {projectCwd && loadError === null && activeSummary !== null ? (
            <div className="flex h-7 shrink-0 items-center justify-between gap-3 border-t border-border/60 px-3 text-[11px] text-muted-foreground">
              <span className="truncate">{activeSummary.left}</span>
              {activeSummary.right !== null ? (
                <span className="shrink-0">{activeSummary.right}</span>
              ) : null}
            </div>
          ) : null}
        </div>
      </SheetPopup>
    </Sheet>
  );
});

export default DocumentsPanel;
export type { DocumentsPanelProps };
